import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#updateScrollHint();
    this.#mutationObserver.takeRecords();
  };

  #updateScrollHint = () => {
    const hint = this.querySelector('[data-filter-scroll-hint]');
    const wrapper = this.querySelector('.facets__inputs-wrapper');
    if (!(hint instanceof HTMLElement) || !(wrapper instanceof HTMLElement)) return;

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const isOpen = this.closest('details')?.open;
    if (isDesktop && isOpen) {
      // Reserve room for the cue and clear button below the scrolling list.
      const availableHeight = window.innerHeight - wrapper.getBoundingClientRect().top - 96;
      this.style.setProperty('--facets-available-height', `${Math.max(80, availableHeight)}px`);
    }
    hint.hidden = !isDesktop || !isOpen || wrapper.scrollHeight - wrapper.clientHeight - wrapper.scrollTop <= 1;
  };

  #scrollResizeObserver = new ResizeObserver(this.#updateScrollHint);

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);
    window.addEventListener('resize', this.#updateScrollHint);
    window.addEventListener('scroll', this.#updateScrollHint, { passive: true });
    this.closest('details')?.addEventListener('toggle', this.#updateScrollHint);
    const wrapper = this.querySelector('.facets__inputs-wrapper');
    if (wrapper instanceof HTMLElement && this.querySelector('[data-filter-scroll-hint]')) {
      wrapper.addEventListener('scroll', this.#updateScrollHint, { passive: true });
      this.#scrollResizeObserver.observe(wrapper);
      if (wrapper.firstElementChild) this.#scrollResizeObserver.observe(wrapper.firstElementChild);
    }

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('resize', this.#updateScrollHint);
    window.removeEventListener('scroll', this.#updateScrollHint);
    this.closest('details')?.removeEventListener('toggle', this.#updateScrollHint);
    this.querySelector('.facets__inputs-wrapper')?.removeEventListener('scroll', this.#updateScrollHint);
    this.#scrollResizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
